/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { createContext, useContext } from 'react';

export interface AgentPanelContextValue {
  closeAgentPanel: () => void;
  isAgentPanelOpen: boolean;
  /** Whether conversation history is shown in place of the conversation. */
  isHistoryOpen: boolean;
  setHistoryOpen: (open: boolean) => void;
  /**
   * Opens the history drawer if needed and puts the caret in its search field.
   * A counter rather than a callback so the drawer can react to repeat requests
   * while it is already open.
   */
  historySearchFocusKey: number;
  focusHistorySearch: () => void;
  /** Hands focus to the mounted chat input (e.g. after a keyboard selection). */
  focusAgentInput: () => void;
  /** Opens a fresh conversation in the side panel and sends the prompt. */
  startAgentConversation: (prompt: string) => void;
  openAgentPanel: (conversationId?: string) => void;
  /**
   * Opens the panel on a fresh conversation, optionally prefilling the chat
   * input (never auto-sent). Use this for entry points that should always start
   * a new thread rather than resume the last one.
   */
  openNewAgentConversation: (draftText?: string) => void;
  toggleAgentPanel: () => void;
}

export const AgentPanelContext = createContext<AgentPanelContextValue | undefined>(undefined);

export const useAgentPanel = () => {
  const context = useContext(AgentPanelContext);
  if (!context) {
    throw new Error('useAgentPanel must be used within AgentPanelProvider');
  }
  return context;
};

export const useOptionalAgentPanel = () => useContext(AgentPanelContext);
